import {
  CompanyDescription,
  CompanyMediaGallery,
  AppLayout,
  BlogHeader,
  PartialContainer,
  ImageKit,
  BreadCrumbs,
} from '@components'
import { queryBlogDetail, queryBlogList } from '@queries'
import { IBlogPost } from '@types'
import { getGraphQLData } from 'database/graphQL/apollo'
import type { GetStaticPaths, GetStaticProps, NextPage } from 'next'
import { useState } from 'react'
import { NextSeo } from 'next-seo'
import Link from 'next/link'
import { getImageUrl, slugify, standardDate } from '@utils'

export const getStaticProps: GetStaticProps = async context => {
  const slug = context.params!.slug as string

  const [gqlResponse] = await Promise.all([
    getGraphQLData<{ blog: IBlogPost }>(queryBlogDetail({ slug })),
  ])

  let post = gqlResponse?.blog

  if (!post) {
    return {
      notFound: true,
    }
  }

  return {
    props: { post: post },
    revalidate: 10,
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  const response = await getGraphQLData<{ allBlogs: IBlogPost[] }>(
    queryBlogList,
  )
  const paths = response?.allBlogs.map(blog => {
    return { params: { slug: blog.slug } }
  })

  return {
    paths,
    fallback: 'blocking',
  }
}

const BlogDetail: NextPage<{ post: IBlogPost }> = ({ post }) => {
  const [active, setActive] = useState('gallery')
  return (
    <AppLayout>
      <NextSeo
        title={post.heading}
        canonical={`https://www.marketxventures.com/news/${post.slug}`}
        description={post.subHeading}
        openGraph={{
          url: `https://www.marketxventures.com/news/${post.slug}`,
          title: post.heading,
          description: post.subHeading,
          images: [
            {
              url: post.bgUrl
                ? getImageUrl({ src: post.bgUrl?.url, width: 800 })
                : '',
              width: post.bgUrl?.width,
              height: post.bgUrl?.height,
              alt: post.bgUrl?.alt,
              type: 'image/jpeg',
            },
          ],
          siteName: `${post.heading} | MarketX Ventures Blog`,
        }}
      />
      <PartialContainer>
        <div className="flex justify-center">
          <div className="mx-auto mb-20 px-2 py-10 text-themeBlack sm:px-6 md:px-8 lg:mx-0 lg:w-[80%]">
            <BreadCrumbs
              pages={[
                { name: 'News', href: '/news' },
                { name: post.category, href: `/${slugify(post.category)}` },
              ]}
            />
            <div className="py-4 lg:py-6">
              <BlogHeader
                heading={post.heading || ''}
                subHeading={post.subHeading || ''}
                sectors={post.sectors || []}
                tags={post.tags || []}
              />
              {post.bgUrl && (
                <div className="relative aspect-19/10">
                  <ImageKit
                    alt={post.name}
                    src={post.bgUrl?.url}
                    fill
                    style={{
                      objectFit: 'cover',
                      objectPosition: 'center',
                    }}
                    placeholder="blur"
                    className="md:pr-4"
                    blurDataURL={post.bgUrl?.blurUpThumb}
                  />
                </div>
              )}
              <CompanyMediaGallery
                gallery={post.mediaGallery}
                setActive={setActive}
              />
              <div className="pt-4">
                {post.authorLink && post.authorName && (
                  <div className="font-bold">
                    <span className="mr-1"> By:</span>
                    <Link legacyBehavior href={post.authorLink}>
                      <a>{post.authorName}</a>
                    </Link>
                  </div>
                )}
                {post.publishingDate && (
                  <h1 className="pt-1 text-sm">
                    {' '}
                    Published: {standardDate(post.publishingDate)}
                  </h1>
                )}
              </div>
              <CompanyDescription
                description={post.modularDescription}
                companySlug={post.slug}
                setActive={setActive}
              />
            </div>
          </div>
        </div>
      </PartialContainer>
    </AppLayout>
  )
}

export default BlogDetail
